import React, { Component } from "react";
import clsx from "clsx";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { withStyles } from "@mui/styles";
import { FormattedMessage, injectIntl } from "react-intl";
import moment from "moment";
import {
  CssBaseline, CircularProgress, Container, Grid, Paper, TextField, Button, MenuItem,
  Chip, IconButton, Tooltip, Pagination, Typography, Box, Divider, Card, CardContent,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import HistoryIcon from "@mui/icons-material/History";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import Api from "../../services/api";
import { browserHistory } from "../../helpers/history";
import * as componentActions from "../../redux/actions/header-menu";
import { Styles } from "../../styles/material-styles";
import { Overlay } from "../../styles/global";
import CustomizedSnackbars from "../../components/material-snackbars";
import HeaderMenu from "../../components/header-menu";
import Footer from "../../components/footer";
import AutomationExecutionDetailDialog from "../../components/automation-execution-detail-dialog";
import { getExecutionsByAutomation, normalizePagedResult } from "../../services/automation-execution.service";

// F3.2 — Historico enriquecido de UMA automation.
// Layout segue o padrao das outras paginas do 2DO: HeaderMenu + main content com
// contentShift/actionscontentShift + drawerHeader (respiro pro header fixo) — assim
// nada fica por baixo do sidebar ou do topo verde.
//
// Regras (CLAUDE.md):
//   - NUNCA async arrow class field (babel 7.12 do react-scripts 4 quebra) — todos os
//     fetch em metodo async normal.
//   - i18n em toda string visivel.
//   - API do 2DO responde JSON lowercase → normalizePagedResult.
const STATUS_COLORS = {
  SUCCESS: { color: "#2e7d32", bg: "#e8f5e9" },
  PARTIAL: { color: "#e65100", bg: "#fff3e0" },
  FAILED_HANDLER: { color: "#c62828", bg: "#ffebee" },
  FAILED_TIMEOUT: { color: "#c62828", bg: "#ffebee" },
  FAILED_UNEXPECTED: { color: "#c62828", bg: "#ffebee" },
  SKIPPED_DEDUP: { color: "#616161", bg: "#f5f5f5" },
  SKIPPED_CONDITION: { color: "#616161", bg: "#f5f5f5" },
};

class AutomationExecutionHistory extends Component {
  constructor(props) {
    super(props);

    const params = new URLSearchParams(window.location.search);
    const automationId =
      (props.match && props.match.params && props.match.params.id) ||
      params.get("id") ||
      (props.location && props.location.state && props.location.state.automationId);

    this.state = {
      automationId: automationId ? parseInt(automationId, 10) : null,
      loading: false,
      page: 1,
      pageSize: 25,
      status: "",
      from: "",
      to: "",
      items: [],
      totalCount: 0,
      totalPages: 0,
      openNotification: false,
      notificationVariant: "info",
      notificationMessage: "",
      selectedBatchId: null,
      detailOpen: false,
    };
  }

  componentDidMount() {
    if (!this.state.automationId) {
      this.notifyError(this.props.intl.formatMessage({ id: "automation.history.error.no.id" }));
      return;
    }
    this.load();
  }

  notifyError = (msg) => {
    this.setState({
      openNotification: true,
      notificationVariant: "error",
      notificationMessage: msg,
    });
  };

  closeNotification = () => this.setState({ openNotification: false });

  handleFilterChange = (e) => {
    const { name, value } = e.target;
    this.setState({ [name]: value });
  };

  applyFilters = () => {
    this.setState({ page: 1 }, this.load);
  };

  clearFilters = () => {
    this.setState({ status: "", from: "", to: "", page: 1 }, this.load);
  };

  onChangePage = (_e, page) => {
    this.setState({ page }, this.load);
  };

  openDetail = (batchId) => {
    this.setState({ selectedBatchId: batchId, detailOpen: true });
  };

  closeDetail = () => {
    this.setState({ detailOpen: false, selectedBatchId: null });
  };

  goBack = () => browserHistory.goBack();

  // Metodo async normal (nao arrow class field) — babel do react-scripts 4 aceita.
  async load() {
    const { automationId, page, pageSize, status, from, to } = this.state;
    if (!automationId) return;

    this.setState({ loading: true });
    try {
      const result = await getExecutionsByAutomation(automationId, { page, pageSize, status, from, to });
      if (result && result.data && result.data.success) {
        const normalized = normalizePagedResult(result.data.data || result.data);
        this.setState({
          items: normalized.items,
          totalCount: normalized.totalCount,
          totalPages: normalized.totalPages,
          loading: false,
        });
      } else {
        this.setState({ loading: false });
        Api.kickoff(result);
      }
    } catch (err) {
      this.setState({ loading: false });
      const msg = (err && err.response && err.response.data && err.response.data.errors && err.response.data.errors[0])
        || this.props.intl.formatMessage({ id: "process.error" });
      this.notifyError(msg);
      Api.kickoff(err);
    }
  }

  renderStatusChip(status) {
    const s = STATUS_COLORS[status] || { color: "#333", bg: "#eee" };
    return (
      <Chip
        size="small"
        label={status || "—"}
        sx={{ backgroundColor: s.bg, color: s.color, fontWeight: 600, borderRadius: 1 }}
      />
    );
  }

  renderTable() {
    const { items } = this.state;
    if (!items || items.length === 0) {
      return (
        <Paper sx={{ p: 4, textAlign: "center", borderRadius: 2, border: "1px solid #e0e0e0" }}>
          <HistoryIcon sx={{ fontSize: 56, color: "#bdbdbd" }} />
          <Typography sx={{ fontSize: 18, fontWeight: 600, mt: 1 }}>
            <FormattedMessage id="automation.history.empty.title" />
          </Typography>
          <Typography sx={{ fontSize: 14, color: "#888", mt: 1 }}>
            <FormattedMessage id="automation.history.empty.description" />
          </Typography>
        </Paper>
      );
    }
    return (
      <Paper sx={{ borderRadius: 2, border: "1px solid #e0e0e0", overflow: "hidden" }}>
        <Box sx={{ display: "grid", gridTemplateColumns: "160px 120px 90px 220px 160px 1fr 60px", background: "#fafafa", px: 2, py: 1.25, fontWeight: 600, fontSize: 13 }}>
          <div><FormattedMessage id="automation.history.col.executed" /></div>
          <div><FormattedMessage id="automation.history.col.status" /></div>
          <div><FormattedMessage id="automation.history.col.duration" /></div>
          <div><FormattedMessage id="automation.history.col.actions.summary" /></div>
          <div><FormattedMessage id="automation.history.col.trigger" /></div>
          <div><FormattedMessage id="automation.history.col.error" /></div>
          <div style={{ textAlign: "center" }}><FormattedMessage id="automation.history.col.view" /></div>
        </Box>
        <Divider />
        {items.map((it) => (
          <Box
            key={it.id}
            sx={{
              display: "grid",
              gridTemplateColumns: "160px 120px 90px 220px 160px 1fr 60px",
              px: 2, py: 1.5, alignItems: "center",
              borderBottom: "1px solid #f0f0f0",
              fontSize: 13,
              "&:hover": { backgroundColor: "#fafafa" },
            }}
          >
            <div>{it.executedAt ? moment(it.executedAt).format("DD/MM/YYYY HH:mm:ss") : "—"}</div>
            <div>{this.renderStatusChip(it.status)}</div>
            <div>{typeof it.durationMs === "number" ? `${it.durationMs}ms` : "—"}</div>
            <div>
              <span style={{ color: "#2e7d32" }}>{it.successfulActions}</span>
              {" / "}
              <span style={{ color: "#c62828" }}>{it.failedActions}</span>
              {" / "}
              <span style={{ color: "#616161" }}>{it.skippedActions}</span>
              {" ("}
              <span>{it.totalActions}</span>
              {")"}
            </div>
            <div style={{ fontFamily: "monospace", fontSize: 12, color: "#555" }}>
              {it.triggerType || "—"}
            </div>
            <div style={{ color: it.errorCode ? "#c62828" : "#888", fontFamily: it.errorCode ? "monospace" : "inherit", fontSize: 12 }}>
              {it.errorCode ? `${it.errorCode}: ${it.errorMessage || ""}`.slice(0, 120) : "—"}
            </div>
            <div style={{ textAlign: "center" }}>
              <Tooltip title={this.props.intl.formatMessage({ id: "automation.history.view.actions" })}>
                <span>
                  <IconButton size="small" disabled={!it.batchId} onClick={() => this.openDetail(it.batchId)}>
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </div>
          </Box>
        ))}
      </Paper>
    );
  }

  render() {
    const { classes, headerMenu, actionsHeaderMenu } = this.props;
    const { loading, status, from, to, page, totalPages, totalCount, detailOpen, selectedBatchId, items } = this.state;
    const automationName = items && items.length > 0 ? (items[0].automationName || "") : "";

    return (
      <div className={classes.root} style={{ paddingLeft: 0, marginLeft: 0 }}>
        <HeaderMenu />
        <main
          className={clsx(classes.content, {
            [classes.contentShift]: headerMenu.open,
          }, classes.actionsContent, {
            [classes.actionscontentShift]: actionsHeaderMenu.open,
          })}
        >
          <div className={classes.drawerHeader} />
          <Container component="main" maxWidth="xl" style={{ maxWidth: "100%", paddingLeft: 0, marginLeft: 0 }}>
            <CssBaseline />

            {/* Barra de acoes topo — padrao do 2DO: botao Voltar como Button outlined
                (mesmo shape das outras paginas edit/registration; nao usar IconButton
                dentro do Card verde do titulo, some visualmente) */}
            <Grid container spacing={2} style={{ marginBottom: 12 }} alignItems="center">
              <Grid item>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<ArrowBackIcon />}
                  onClick={this.goBack}
                >
                  <FormattedMessage id="back" />
                </Button>
              </Grid>
              <Grid item xs />
              <Grid item>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<RefreshIcon />}
                  onClick={() => this.load()}
                >
                  <FormattedMessage id="automation.history.reload" />
                </Button>
              </Grid>
            </Grid>

            {/* Cabecalho da pagina — Card verde padrao (background-title-search) */}
            <Grid container spacing={0} style={{ marginBottom: 16 }}>
              <Grid item xs={12}>
                <Card>
                  <CardContent className="background-title-search">
                    <Typography variant="h5" sx={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 1 }}>
                      <HistoryIcon /> <FormattedMessage id="automation.history.title" />
                      {automationName && (
                        <Typography component="span" sx={{ color: "#333", fontWeight: 400, fontSize: 16, ml: 1 }}>
                          — {automationName}
                        </Typography>
                      )}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#333", mt: 0.5 }}>
                      <FormattedMessage id="automation.history.subtitle" />
                      {totalCount > 0 && <span> ({totalCount})</span>}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Filtros */}
            <Paper sx={{ p: 2, borderRadius: 2, mb: 2, border: "1px solid #e0e0e0" }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth select label={this.props.intl.formatMessage({ id: "automation.history.filter.status" })}
                    name="status" value={status} onChange={this.handleFilterChange} size="small"
                    InputLabelProps={{ shrink: true }}
                  >
                    <MenuItem value="">{this.props.intl.formatMessage({ id: "all" })}</MenuItem>
                    <MenuItem value="SUCCESS">Sucesso</MenuItem>
                    <MenuItem value="PARTIAL">Parcial</MenuItem>
                    <MenuItem value="FAILED_HANDLER">Falha (handler)</MenuItem>
                    <MenuItem value="FAILED_TIMEOUT">Falha (timeout)</MenuItem>
                    <MenuItem value="FAILED_UNEXPECTED">Falha (inesperada)</MenuItem>
                    <MenuItem value="SKIPPED_DEDUP">Ignorada (dedup)</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth type="date" name="from" value={from}
                    label={this.props.intl.formatMessage({ id: "automation.history.filter.from" })}
                    onChange={this.handleFilterChange} size="small" InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth type="date" name="to" value={to}
                    label={this.props.intl.formatMessage({ id: "automation.history.filter.to" })}
                    onChange={this.handleFilterChange} size="small" InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3} sx={{ display: "flex", gap: 1 }}>
                  <Button fullWidth variant="contained" onClick={this.applyFilters}>
                    <FormattedMessage id="automation.history.filter.apply" />
                  </Button>
                  <Button fullWidth variant="text" onClick={this.clearFilters}>
                    <FormattedMessage id="automation.history.filter.clear" />
                  </Button>
                </Grid>
              </Grid>
            </Paper>

            {this.renderTable()}

            {totalPages > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 3, mb: 3 }}>
                <Pagination count={totalPages} page={page} onChange={this.onChangePage} color="primary" />
              </Box>
            )}

            {loading && (
              <Overlay>
                <CircularProgress color="secondary" />
              </Overlay>
            )}

            <CustomizedSnackbars
              variant={this.state.notificationVariant}
              message={this.state.notificationMessage}
              isOpen={this.state.openNotification}
              toClose={this.closeNotification}
            />

            <AutomationExecutionDetailDialog
              open={detailOpen}
              batchId={selectedBatchId}
              onClose={this.closeDetail}
            />
          </Container>
        </main>
        <Footer />
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  actionsHeaderMenu: state.actionsHeaderMenu,
  headerMenu: state.headerMenu,
  userSession: state.userSession,
});
const mapDispatchToProps = (dispatch) => bindActionCreators(componentActions, dispatch);
export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(withStyles(Styles)(AutomationExecutionHistory)));
